import type { CSSProperties, ReactNode } from 'react';

export type DataTableColumn<T> = {
  key: string;
  label: string;
  render?: (value: unknown, row: T) => ReactNode;
};

type Props<T> = {
  columns: Array<DataTableColumn<T>>;
  rows: T[];
  loading?: boolean;
  emptyMessage?: string;
  getRowProps?: (
    row: T,
    index: number,
  ) => { className?: string; style?: CSSProperties; onClick?: () => void } | undefined;
};

function readCell<T>(row: T, key: string): unknown {
  return (row as Record<string, unknown>)[key];
}

export function DataTable<T extends { id?: string | number; row_key?: string | number }>({
  columns,
  rows,
  loading = false,
  emptyMessage = 'Nessun dato disponibile',
  getRowProps,
}: Props<T>) {
  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeRows = Array.isArray(rows) ? rows : [];

  if (loading) {
    return <p className="sk-saas-hint">Caricamento dati…</p>;
  }

  if (safeRows.length === 0) {
    return <p className="sk-saas-hint">{emptyMessage}</p>;
  }

  return (
    <div className="sk-data-table-wrap">
      <table className="sk-data-table">
        <thead>
          <tr>
            {safeColumns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {safeRows.map((row, idx) => {
            const rowProps = typeof getRowProps === 'function' ? getRowProps(row, idx) || {} : {};
            const mergedClassName = ['sk-data-table__row', rowProps.className || '']
              .filter(Boolean)
              .join(' ');
            return (
              <tr
                key={String(row.row_key ?? row.id ?? idx)}
                className={mergedClassName}
                style={rowProps.style}
                onClick={rowProps.onClick}
              >
                {safeColumns.map((column) => (
                  <td key={column.key}>
                    {column.render
                      ? column.render(readCell(row, column.key), row)
                      : String(readCell(row, column.key) ?? '—')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
